import api from '../../../../api';
import Botservice from '../../../../Service/Botservice';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../../Service/crypto';
import { botRuntimeFetch, ensureBotRuntimeAuth, getBotAccessToken } from '../../../../Service/botRuntimeService';

const getReadableErrorMessage = async (response: Response, fallbackMessage: string) => {
  const contentType = response.headers.get('content-type') || '';

  try {
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (typeof data?.message === 'string' && data.message.trim()) return data.message;
      if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
      if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    } else {
      const text = await response.text();
      if (text && !text.trim().startsWith('<!DOCTYPE html') && !text.trim().startsWith('<html')) {
        return text.slice(0, 200);
      }
    }
  } catch {
  }

  return fallbackMessage;
};

export interface SaveInstructionStep {
  action: string;
  description: string;
  className?: string;
  id?: string;
  tag?: string;
  xpath?: string;
  input_type?: string;
  placeholder?: string;
  Detailed_description?: string;
}

export interface SaveCollectionStep {
  step: number;
  action: string;
  prompt: string;
  Detailed_description?: string;
  selection_strategy?: 'list' | '';
  optional?: boolean;
  dynamic_instruction?: string;
  element: {
    id: string;
    class: string;
    tagname: string;
    xpath: string;
    input_type?: string;
    placeholder?: string;
  };
}

export interface SaveInstructionsPayload {
  userId: string;
  username: string;
  useremail: string;
  sessionId: string;
  projectTitle: string;
  url: string;
  description: string;
  instruction: Record<string, SaveInstructionStep>;
  steps?: SaveCollectionStep[];
}

export interface GenerateInstructionsPayload {
  url: string;
  userId: string;
  sessionId: string;
  projectTitle: string;
  instruction: string[];
  xpath: string[];
  tags: string[];
  class_name: string[];
  id: string[];
  input_type: string[];
  placeholder: string[];
  file_name: string | null;
  file_type: string | null;
}

export const executeBotServiceConnection = async (userId: string): Promise<void> => {
  await ensureBotRuntimeAuth(userId);
};

export const getUserActivityWsBase = () =>
  Botservice.replace(/^https/, 'wss').replace(/^http/, 'ws');

export const getUserActivityWsUrl = async (sessionId: string, targetUrl: string, userId?: string) => {
  const token = getBotAccessToken() || (userId ? await ensureBotRuntimeAuth(userId) : '');
  const baseUrl = `${getUserActivityWsBase()}ws/userlog/${sessionId}/${encodeURIComponent(targetUrl)}`;
  return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
};

export const startUserActivityStream = async (payload: {
  url: string;
  project_title: string;
  session_id: string;
  userid: string;
  username: string;
}) => {
  const response = await botRuntimeFetch('/user_log/start', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, payload.userid);

  if (!response.ok) {
    const errorMessage = await getReadableErrorMessage(
      response,
      'Failed to start user activity stream. The streaming endpoint may not be available in IVP backend.'
    );
    throw new Error(errorMessage);
  }

  return response.json();
};

export const generateUserActivityInstructions = async (
  userId: string,
  payload: GenerateInstructionsPayload
) => {
  const response = await botRuntimeFetch(`/user_log/prompt-instruction/${userId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, userId);

  if (!response.ok) {
    const errorMessage = await getReadableErrorMessage(
      response,
      `Failed to generate instructions. HTTP ${response.status}`
    );
    throw new Error(errorMessage);
  }

  return response.json();
};

export const saveUserActivityInstructions = async (
  payload: SaveInstructionsPayload
) => {
  const key = await keyPromise;
  const EncParams = await AesEncrypt(key, payload);
  const response = await api.fetch('api/activity/save-instruction', {
    method: 'POST',
    body: JSON.stringify({ EncParams }),
  });

  if (!response.ok) {
    const errorMessage = await getReadableErrorMessage(response, 'Failed to save instructions');
    throw new Error(errorMessage);
  }

  const raw = await response.json();
  return AesDecrypt(key, raw);
};

export const uploadUserActivityFile = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const userId = localStorage.getItem('id') || '';
  const token = getBotAccessToken() || (userId ? await ensureBotRuntimeAuth(userId) : '');
  const response = await fetch(`${Botservice}upload_file`, {
    method: 'POST',
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!response.ok) {
    const errorMessage = await getReadableErrorMessage(response, `Failed to upload file. HTTP ${response.status}`);
    throw new Error(errorMessage);
  }

  return response.json();
};
